import React from 'react';
import {
  Progress,
  Text,
  Group,
  Alert,
  Loader,
  ActionIcon,
} from '@mantine/core';
import { IconX, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { CORE_NS } from 'recruiter-i18n/namespaces/core';
import { SHELL_NS } from 'recruiter-i18n/namespaces/shell';
import { BulkOperationStatus } from 'api/generated/types';

import type { OperationProgress } from '../../context-providers/bulkOperationTypes';

import './BulkOperationToast.css';

interface BulkOperationToastProps {
  operation: OperationProgress;
  onClose: () => void;
  showDetails?: boolean;
}

/**
 * Pure presentation component for displaying bulk operation progress
 * Reads progress from context and displays it - does not manage SSE connections
 */
export const BulkOperationToast: React.FC<BulkOperationToastProps> = ({
  operation,
  onClose,
  showDetails = true,
}) => {
  const { t } = useTranslation([SHELL_NS, CORE_NS]);
  const {
    title,
    status,
    totalCount,
    processedCount,
    successCount,
    failedCount,
    error,
  } = operation;

  // Calculate progress percentage
  const progressPercentage =
    totalCount > 0 ? (processedCount / totalCount) * 100 : 0;

  // Determine display title
  const displayTitle = title || t('core:status.processing');

  const isComplete =
    status === BulkOperationStatus.COMPLETED ||
    status === BulkOperationStatus.FAILED ||
    status === BulkOperationStatus.CANCELLED;

  const isSuccess =
    status === BulkOperationStatus.COMPLETED && failedCount === 0;
  const isPartialSuccess =
    status === BulkOperationStatus.COMPLETED &&
    failedCount > 0 &&
    successCount > 0;
  const isFailed =
    status === BulkOperationStatus.FAILED ||
    (status === BulkOperationStatus.COMPLETED && successCount === 0);

  return (
    <div className='bulk-operation-toast'>
      <div className='bulk-operation-toast-header'>
        <Group justify='space-between' w='100%'>
          <Group gap='xs'>
            {!isComplete && <Loader size='sm' />}
            {isSuccess && <IconCheck size={20} color='green' />}
            {(isPartialSuccess || isFailed) && (
              <IconAlertCircle
                size={20}
                color={isPartialSuccess ? 'orange' : 'red'}
              />
            )}
            <Text size='sm' fw={600}>
              {displayTitle}
            </Text>
          </Group>
          {isComplete && (
            <ActionIcon
              variant='subtle'
              color='gray'
              size='sm'
              onClick={onClose}
              aria-label={t('core:actions.close')}
            >
              <IconX size={16} />
            </ActionIcon>
          )}
        </Group>
      </div>

      <div className='bulk-operation-toast-body'>
        {/* Progress bar */}
        <Progress
          value={progressPercentage}
          size='sm'
          color={
            isFailed
              ? 'red'
              : isPartialSuccess
                ? 'yellow'
                : isSuccess
                  ? 'green'
                  : 'blue'
          }
          animated={!isComplete}
        />

        {/* Details */}
        {showDetails && totalCount > 0 && (
          <Group justify='space-between' mt='xs'>
            <Text size='xs' c='dimmed'>
              {t('bulkToast.processedOf', {
                processed: processedCount,
                total: totalCount,
              })}
            </Text>
            {failedCount > 0 && (
              <Text size='xs' c='red'>
                {t('bulkToast.failedCount', { count: failedCount })}
              </Text>
            )}
          </Group>
        )}

        {/* Error message */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title={t('core:status.error')}
            color='red'
            mt='xs'
          >
            <Text size='xs'>{error}</Text>
          </Alert>
        )}

        {/* Completion message */}
        {isComplete && !error && (
          <Text size='xs' c='dimmed' mt='xs'>
            {isSuccess && t('bulkToast.success', { count: successCount })}
            {isPartialSuccess &&
              t('bulkToast.partial', {
                count: successCount,
                failed: failedCount,
              })}
            {isFailed && t('bulkToast.failed')}
          </Text>
        )}
      </div>
    </div>
  );
};
